import { useState } from 'react';
import axios from 'axios';
import type { DataResponse, PollAnswerRequest, PollView } from '../contracts';
import { useMutation, useQuery } from 'react-query';
import Button from '../components/ui/Button';
import { ErrorNotice, LoadingPanel } from '../components/ui/States';

const publicApi = axios.create({ baseURL: import.meta.env.VITE_API_URL ?? '/api', timeout: 30000 });

const slotLabel = (iso: string, lang: 'en' | 'fr') =>
  new Date(iso).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    timeZone: 'America/Toronto',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** One-use availability link for a committee member: no login, the link is the key. */
export default function Availability() {
  const token = window.location.pathname.split('/availability/')[1]?.split('/')[0] ?? '';
  const [picked, setPicked] = useState<string[]>([]);
  const poll = useQuery<PollView>(
    ['availability', token],
    async () => (await publicApi.get<DataResponse<PollView>>(`/availability/${token}`)).data.data,
    { retry: false },
  );
  const save = useMutation(async () =>
    publicApi.post(`/availability/${token}`, { choices: picked } satisfies PollAnswerRequest),
  );
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="min-h-screen bg-paper flex items-start justify-center p-8">
      <div className="bg-white border border-line max-w-[640px] w-full p-8">
        <div className="text-small text-ink-2 mb-2">DIVERT Mental Health · DIVERT santé mentale</div>
        {poll.isLoading ? <LoadingPanel label="Loading…" height={120} /> : null}
        {poll.isError ? <ErrorNotice>This link is not valid. / Ce lien n’est pas valide.</ErrorNotice> : null}
        {poll.data ? (
          <>
            <h1 className="font-serif font-semibold text-[26px] leading-tight">{poll.data.title}</h1>
            <p className="text-table text-ink-2 mt-1">
              {poll.data.committee} · {poll.data.committeeFr}
            </p>
            {save.isSuccess || poll.data.answered ? (
              <p className="text-table mt-6">
                Thank you, your answer is recorded. / Merci, votre réponse est enregistrée.
              </p>
            ) : !poll.data.open ? (
              <p className="text-table mt-6">This poll is closed. / Ce sondage est fermé.</p>
            ) : (
              <>
                <p className="text-table mt-5">
                  Tick every time that works for you (Eastern Time). / Cochez toutes les plages qui vous conviennent
                  (heure de l’Est).
                </p>
                <ul className="mt-4 space-y-2">
                  {poll.data.slots.map((s) => (
                    <li key={s.id}>
                      <label className="flex items-start gap-3 text-table cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={picked.includes(s.id)}
                          onChange={() => toggle(s.id)}
                        />
                        <span>
                          {slotLabel(s.startsAt, 'en')}
                          <span className="block text-small text-ink-2">{slotLabel(s.startsAt, 'fr')}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <p className="text-small text-ink-2 mt-4">This link works once. / Ce lien ne fonctionne qu’une fois.</p>
                <div className="mt-5">
                  <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
                    {save.isLoading
                      ? 'Sending…'
                      : picked.length
                        ? 'Send / Envoyer'
                        : 'None of these work / Aucune ne convient'}
                  </Button>
                </div>
                {save.isError ? (
                  <div className="mt-3">
                    <ErrorNotice>Your answer was not saved. / Votre réponse n’a pas été enregistrée.</ErrorNotice>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
